import { useCallback, useEffect, useRef, useState } from "react";
import { getEnrollmentAmount } from "../api/server";

// Start during the introductory confirmation and reuse the quote at payment.
export default function useEnrollmentPrice(user, course, modalId, adminUserId, initialModalId, initialQuote) {
  const code = String(user?.[`coupon_${course}`] || user?.[`couponcode_${course}`] || "").trim();
  const needsPrice = !!user && !!code &&
    user[`paid_${course}`] !== "checked" &&
    !(course === "first" && user.google_add === "1") &&
    ["client", "", course === "first" ? "DE" : "CC"].includes(user.billingoptions);
  const seededQuote = initialQuote?.studentId === adminUserId && initialQuote?.course === course &&
    initialQuote?.code === code && Number.isFinite(initialQuote?.amount) && initialQuote.amount >= 0
    ? initialQuote : null;
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const started = useRef(false);
  const request = useRef(0);
  const start = useCallback(() => {
    if (seededQuote) return;
    started.current = true;
    setQuote(null);
    setError("");
    setOpen(true);
    setAttempt(value => value + 1);
  }, [seededQuote]);
  const fetchPrice = useCallback(async () => {
    if (!needsPrice) return;
    const version = ++request.current;
    setQuote(null);
    setError("");
    try {
      const response = await getEnrollmentAmount(code, adminUserId ? "admin" : "user");
      const raw = response.data?.amount;
      const amount = Number(raw);
      if ((typeof raw !== "number" && typeof raw !== "string") ||
          String(raw).trim() === "" || !Number.isFinite(amount) || amount < 0) {
        throw new Error("Invalid course amount");
      }
      if (version === request.current) setQuote({ code, amount });
    } catch (err) {
      if (version === request.current) {
        setError("Unable to load the latest course price. Please go back and try again.");
      }
    }
  }, [needsPrice, code, adminUserId]);

  useEffect(() => {
    const onShow = (event) => {
      if (event.target.id === initialModalId ||
          (event.target.id === modalId && !started.current)) {
        start();
      }
    };
    const onHide = (event) => {
      if (event.target.id === modalId) {
        started.current = false;
        setOpen(false);
      }
    };
    document.addEventListener("show.bs.modal", onShow);
    document.addEventListener("hide.bs.modal", onHide);
    return () => {
      document.removeEventListener("show.bs.modal", onShow);
      document.removeEventListener("hide.bs.modal", onHide);
      request.current += 1;
    };
  }, [modalId, initialModalId, start]);

  useEffect(() => {
    if (open) fetchPrice();
    return () => { request.current += 1; };
  }, [open, fetchPrice, attempt]);

  const currentQuote = quote || seededQuote;
  const ready = currentQuote !== null && currentQuote.code === code;
  return {
    start,
    data: needsPrice ? { ...user, amount: ready ? currentQuote.amount : null, totalAmount: ready ? currentQuote.amount : null } : user,
    blocked: needsPrice && !ready,
    label: needsPrice ? (ready ? currentQuote.amount.toFixed(2) : "…") :
      (!code && user?.amount != null ? Number(user.amount).toFixed(2) : null),
    error: needsPrice ? error : "",
  };
}
